import { useState } from "react";
import { FileDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { currentMonthIST, monthLabelIST } from "../../lib/datetime";

/**
 * The one-button monthly activity report (docs/specs/MONTHLY_REPORT_2026-09-08.md
 * §4). One component for all three dashboards, so the officer, the manager and
 * the Director press the same control and get the same PDF for their scope —
 * the SERVER decides whose pages are in it, never this file.
 *
 * Pick a month (default: the last complete month, IST), press Download, and
 * `GET /reports/monthly` comes back as a PDF the browser saves. The request
 * goes through `api` so the Bearer / cookie auth the rest of the app uses
 * applies; the bytes are handed to the browser as a blob. A failed download is
 * printed under the button — a silent no-op on a phone is indistinguishable
 * from a slow one.
 */

const MONTHS_OFFERED = 12;

/** The last twelve COMPLETE months, newest first, as `{ key, label }`. */
export function completedMonthsIST({ now = currentMonthIST() } = {}) {
  const out = [];
  let { year, month } = now;
  for (let i = 0; i < MONTHS_OFFERED; i++) {
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    out.push({
      key: `${year}-${String(month).padStart(2, "0")}`,
      label: monthLabelIST(year, month),
    });
  }
  return out;
}

export default function MonthlyReportButton() {
  const months = completedMonthsIST();
  const [month, setMonth] = useState(months[0].key);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.get("/reports/monthly", {
        params: { month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `monthly-report-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // A blob response carries its error detail as a blob too.
      let detail = e?.response?.data;
      if (detail && typeof detail.text === "function") {
        try {
          detail = JSON.parse(await detail.text())?.detail;
        } catch {
          detail = null;
        }
      } else {
        detail = detail?.detail;
      }
      setError(formatApiErrorDetail(detail ?? e?.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-callisto bg-white p-4 shadow-sm"
      data-testid="monthly-report"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-callisto-navy">Monthly report</p>
          <p className="text-[11px] text-callisto-body">
            Activity recorded in the app for the month, as a PDF: the company
            summary, then one page per person your role can see.
          </p>
        </div>
        <label className="sr-only" htmlFor="monthly-report-month">
          Report month
        </label>
        <select
          id="monthly-report-month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 rounded-md border border-callisto bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-callisto-gold"
          data-testid="monthly-report-month"
        >
          {months.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-callisto-navy px-4 text-sm font-bold text-white hover:bg-callisto-navy/90 disabled:opacity-60"
          data-testid="monthly-report-download"
        >
          <FileDown className="h-4 w-4 text-callisto-gold" />
          {busy ? "Preparing…" : "Download PDF"}
        </button>
      </div>
      {error && (
        <p
          className="mt-2 text-xs text-red-700"
          data-testid="monthly-report-error"
        >
          The report did not download — {error}
        </p>
      )}
    </div>
  );
}
